import { ErrorMessage, Field, Form, Formik } from 'formik';
import {
    Box, Button, FormControl, Grid, IconButton, InputLabel, MenuItem,
    Select,
    Stack, TextField, Tooltip, useMediaQuery
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { koReportingSearchOption, koReportOption } from '../../../../app/common/options/koReportingOpt';
import { KoReportFormValues } from '../../../../app/models/dataErasure';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import { toast } from 'react-toastify';
import CircularProgress from '@mui/material/CircularProgress';
import { observer } from 'mobx-react-lite';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

interface IProps {
    koDataErasureFilter: (formValues: KoReportFormValues) => void;
    resetFormHandler: () => void;
    isChecked: boolean;
    downloadTemplate: (selOption: string) => void;
    excelLoading: boolean;
}

const initialFormvalues: KoReportFormValues = {
    reportType: 'Registrations',
    searchBy: 'PickupNo',
    searchText: '',
    startDate: new Date(),
    endDate: new Date(),
}

const root = (isMobile: boolean) => {
    return {
        margin: '0 auto',
        width: '100%',
        overflow: 'hidden',
        padding: 1,
        boxShadow: '3px 3px 7px 1px #ccc',
        background: '#DAE5F1',
        borderRadius: '10px',
        '& h5': {
            fontWeight: 500,
        },
        '& .MuiInputBase-input': {
            color: '#000'
        },
        '& .MuiFormLabel-root': {
            color: '#000'
        },
        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderColor: '#000'
        },
        "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000"
        },
        '& .MuiSelect-iconOutlined': {
            color: '#000'
        },
    }
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};
const KoReportForm: React.FC<IProps> = ({ koDataErasureFilter, resetFormHandler,
    isChecked, downloadTemplate, excelLoading }) => {

    const matches = useMediaQuery('(min-width:900px)');

    return (
        <Box component='div'
            sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Formik
                    initialValues={initialFormvalues}
                    onSubmit={(values: KoReportFormValues) => {
                        if (((values.searchBy.includes("Date") && (!values.startDate || !values.endDate))
                            || ((values.searchBy.includes("Date") && values.startDate > values.endDate))
                            || ((!values.searchBy.includes("Date") && !values.searchText)))) {
                            toast.error("Please enter required values");
                        }
                        else {
                            koDataErasureFilter(values)
                        }
                    }}
                >
                    {({ values, handleChange, handleBlur, isSubmitting, setFieldValue }) => (
                        <Form autoComplete='off'>
                            <Box component='div' sx={root(matches)}>
                                <Stack direction={matches ? "row" : "column"} spacing={2} p={2}>
                                    <FormControl sx={{ width: 300 }}>
                                        <InputLabel id="reportType-label">Report</InputLabel>
                                        <Select
                                            labelId="reportType-label"
                                            id="reportType"
                                            name="reportType"
                                            value={values.reportType}
                                            label="Report"
                                            onChange={handleChange}
                                            sx={{ minWidth: 180 }}
                                            MenuProps={MenuProps}
                                        >
                                            {koReportOption.map((report) => (
                                                <MenuItem value={report.value} key={report.key}>{report.text}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl sx={{ width: 300 }}>
                                        <InputLabel id="searchBy-label">Search by</InputLabel>
                                        <Select
                                            labelId="searchBy-label"
                                            id="searchBy"
                                            name="searchBy"
                                            value={values.searchBy}
                                            label="Searchby"
                                            onChange={handleChange}
                                            sx={{ minWidth: 180 }}
                                            MenuProps={MenuProps}
                                        >
                                            {koReportingSearchOption.map((search) => (
                                                <MenuItem value={search.value} key={search.key}>{search.text}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                </Stack>
                                <Stack direction={matches ? "row" : "column"} spacing={2} m={2} alignItems='baseline'>
                                    {(values.searchBy === 'PickupNo' || values.searchBy === 'Reference' || values.searchBy === 'City'
                                        || values.searchBy === 'Country'
                                    ) &&
                                        <FormControl fullWidth>
                                            <Field
                                                type="text"
                                                component={TextField}
                                                id="searchText"
                                                name="searchText"
                                                placeholder="Search"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.searchText}
                                                variant="outlined"
                                            />
                                            <ErrorMessage className="error" name="searchText" component="div" />
                                        </FormControl>
                                    }

                                    {(values.searchBy === 'CollectionDate' || values.searchBy === 'ReportDate')
                                        &&
                                        <>
                                            <FormControl sx={{ width: 300 }}>
                                                <DatePicker
                                                    label="Start Date"
                                                    // openTo="year"                                                    
                                                    views={['year', 'month', 'day']}
                                                    value={values.startDate}
                                                    onChange={(value) => {
                                                        if (value)
                                                            setFieldValue('startDate', dayjs(value));
                                                    }}
                                                    renderInput={(params) => <TextField {...params} />}
                                                />
                                                <ErrorMessage
                                                    className="error"
                                                    name="startDate"
                                                    component="span"
                                                />
                                            </FormControl >
                                            <FormControl sx={{ width: 300 }}>
                                                <DatePicker
                                                    label="End Date"
                                                    // openTo="year"                                                    
                                                    views={['year', 'month', 'day']}
                                                    value={values.endDate}
                                                    onChange={(value) => {
                                                        if (value)
                                                            setFieldValue('endDate', dayjs(value));
                                                    }}
                                                    renderInput={(params) => <TextField {...params} />}
                                                />
                                                <ErrorMessage
                                                    className="error"
                                                    name="endDate"
                                                    component="span"
                                                />
                                            </FormControl >
                                        </>
                                    }
                                </Stack>
                                {matches ?
                                    <Grid container columnSpacing={2} sx={{ width: 500, height: 50, textAlign: 'center' }}>

                                        {excelLoading &&
                                            <Grid item sm={12}>
                                                <CircularProgress size='1.4rem' />
                                            </Grid>
                                        }
                                        {!excelLoading &&
                                            <>
                                                <Grid item lg={4}>
                                                    <Button size="medium" variant="contained"
                                                        endIcon={<SearchOutlinedIcon />} type='submit'>Search</Button>
                                                </Grid>
                                                <Grid item lg={4}>
                                                    <Button disabled={!isChecked || excelLoading}
                                                        size="medium" variant="contained"
                                                        endIcon={<CloudDownloadOutlinedIcon />}
                                                        type="button" onClick={() => downloadTemplate(values.reportType)}>Download</Button>

                                                </Grid>
                                                <Grid item lg={4}>
                                                    <Button size="medium" variant="contained"
                                                        endIcon={<RotateLeftIcon />}
                                                        type="button" onClick={resetFormHandler}>Reset</Button>
                                                </Grid>
                                            </>
                                        }
                                    </Grid>
                                    :
                                    <Grid container spacing={0} sx={{ textAlign: 'center' }}>

                                        {excelLoading &&
                                            <Grid item sm={10}>
                                                <CircularProgress size='2.1rem'/>
                                            </Grid>
                                        }
                                        {!excelLoading &&
                                            <>
                                                <Grid item xs={4}>
                                                    <Tooltip title="Search" placement='right'>
                                                        <IconButton type='submit' >
                                                            <SearchOutlinedIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Tooltip title="Download" placement='right'>
                                                        <IconButton onClick={() => downloadTemplate(values.reportType)} disabled={!isChecked}>
                                                            <CloudDownloadOutlinedIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Tooltip title="Reset" placement='right'>
                                                        <IconButton onClick={resetFormHandler}>
                                                            <RotateLeftIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>
                                            </>
                                        }
                                    </Grid>}

                            </Box>
                        </Form>
                    )}
                </Formik>
            </LocalizationProvider>
        </Box >
    )
}
export default observer(KoReportForm);